import { createElement, type ReactNode } from 'react';
import type { ConfigType } from '@plone/registry';
import { joinWithPreviousBlock } from '@plone/volto-slate/blocks/Text/keyboard/joinBlocks';

import withSubHomeCard from 'volto-ministerio-publico-federal/components/Blocks/SubHomeText/withSubHomeCard';
import withSafeTableCells from 'volto-ministerio-publico-federal/components/Blocks/SlateTable/withSafeTableCells';
import withParagrafoVazio from 'volto-ministerio-publico-federal/components/Blocks/Text/withParagrafoVazio';
import withEmailsClicaveis from 'volto-ministerio-publico-federal/components/Blocks/Text/withEmailsClicaveis';
import withEmailsClicaveisHtml from 'volto-ministerio-publico-federal/components/Blocks/Text/withEmailsClicaveisHtml';
import withPreviousBlockGuard from 'volto-ministerio-publico-federal/helpers/Slate/joinWithPreviousBlock';
import {
  LEAF_ALTERADO,
  LEAF_REMOVIDO,
} from 'volto-ministerio-publico-federal/helpers/MarcacoesRevisao';

/** O bloco de texto do volto-slate e o `text` legado. */
const TEXT_BLOCKS = ['slate', 'text'];
const TABLE_BLOCK = 'slateTable';

type KeyboardHandler = ((...args: any[]) => unknown) & { name?: string };

/**
 * O texto corrido ganha um cartão com fundo quando a página é uma sub-home
 * (anotação do Figma). Envolver a `view` dos blocos de texto do core é mais
 * barato do que copiar o editor do volto-slate para cá — ver
 * `Blocks/SubHomeText/withSubHomeCard.tsx`. Blocos ausentes no registro são
 * ignorados.
 *
 * Só a `view`, de propósito. A `edit` do volto-slate é um componente conectado
 * com gestão própria de foco e seleção; um nó a mais em volta dela é risco
 * desnecessário para um ganho só cosmético (a canvas do editor já difere da
 * página em várias outras coisas). O fundo aparece no modo de leitura, que é o
 * que o design especifica.
 */
function applySubHomeCard(config: ConfigType) {
  for (const type of TEXT_BLOCKS) {
    const block = config.blocks.blocksConfig[type];
    if (block?.view) block.view = withSubHomeCard(block.view);
  }
}

/**
 * Célula de tabela que o editor do volto-slate não sabe abrir derruba o
 * formulário inteiro. Acontece com o conteúdo migrado e a cada colagem de HTML
 * com `<td>` vazio — ver `Blocks/SlateTable/withSafeTableCells.tsx`.
 */
function applySafeTableCells(config: ConfigType) {
  const block = config.blocks.blocksConfig[TABLE_BLOCK];
  if (block?.edit) block.edit = withSafeTableCells(block.edit);
}

/**
 * O Backspace no começo de um parágrafo derruba o editor quando o bloco
 * anterior não é de texto — ver `helpers/Slate/joinWithPreviousBlock.ts`.
 *
 * O handler do core é trocado pela versão guardada por identidade de módulo; o
 * nome serve de rede para o caso de o bundler entregar outra instância.
 */
function applyBackspaceGuard(config: ConfigType) {
  const slate = (config.settings as Record<string, any>)?.slate;
  const handlers: KeyboardHandler[] | undefined =
    slate?.textblockKeyboardHandlers?.Backspace;
  if (!Array.isArray(handlers)) return;

  const isCoreJoin = (handler: KeyboardHandler) =>
    handler === joinWithPreviousBlock ||
    handler?.name === 'joinWithPreviousBlock';

  slate.textblockKeyboardHandlers.Backspace = handlers.map(
    (handler: KeyboardHandler) =>
      isCoreJoin(handler) ? withPreviousBlockGuard(handler as any) : handler,
  );
}

/**
 * Marca-texto da revisão: a view da notícia marca as folhas que os revisores
 * alteraram com `mpfAlterado` e devolve o texto excluído como folha
 * `mpfRemovido`, riscada (ver `helpers/MarcacoesRevisao`). Só existe na
 * cópia usada para exibir; o valor salvo nunca tem essa propriedade.
 */
function applyLeafAlterado(config: ConfigType) {
  const slate = (config.settings as Record<string, any>)?.slate;
  if (!slate?.leafs) return;
  slate.leafs[LEAF_ALTERADO] = ({ children }: { children: ReactNode }) =>
    createElement('mark', { className: 'mpf-alterado' }, children);
  slate.leafs[LEAF_REMOVIDO] = ({ children }: { children: ReactNode }) =>
    createElement('del', { className: 'mpf-removido' }, children);
}

/**
 * O conteúdo migrado chega cheio de parágrafos só com quebra de linha, que o
 * tema desenhava como linhas em branco com margem (bug 11394, N13) — ver
 * `Blocks/Text/withParagrafoVazio.tsx`. O `p` do core é envolvido para
 * ganhar a classe que o CSS esconde; os demais elementos ficam como estão.
 */
/**
 * E-mails escritos como texto no conteúdo migrado viram link `mailto:` na
 * leitura (AB#11165) — ver `Blocks/Text/withEmailsClicaveis.tsx`. Só a
 * `view` do bloco `slate`, pela mesma razão do cartão da sub-home.
 */
function applyEmailsClicaveis(config: ConfigType) {
  const slate = config.blocks.blocksConfig.slate;
  if (slate?.view) slate.view = withEmailsClicaveis(slate.view);
  // O conteúdo migrado que a carga ainda não converteu continua em bloco `html`.
  const html = config.blocks.blocksConfig.html;
  if (html?.view) html.view = withEmailsClicaveisHtml(html.view);
}

function applyParagrafoVazio(config: ConfigType) {
  const elements = (config.settings as Record<string, any>)?.slate?.elements;
  if (elements?.p) elements.p = withParagrafoVazio(elements.p);
}

/**
 * Ajustes do editor do volto-slate: o que o core registra e nós envolvemos,
 * seja para seguir o design, seja para tapar buraco de bloco quebrado.
 */
export const updateSlateConfig = (config: ConfigType) => {
  applySubHomeCard(config);
  applySafeTableCells(config);
  applyBackspaceGuard(config);
  applyLeafAlterado(config);
  applyParagrafoVazio(config);
  applyEmailsClicaveis(config);
  return config;
};

export default updateSlateConfig;
